import { useCallback } from "react";
import { generateMarketingPage } from "@/network/network";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { ASYNC_TASK_TYPES, VIEW_TYPES } from "@/other/constants/OtherKeys";
import { showToast } from "@/other/utils/utils";
import { getApiErrorMessage } from "@/integrations";
import { useAsyncTaskContext } from "@/modern/components/async-tasks/context/AsyncTaskContext";
import { useAsyncTaskUIContext } from "@/modern/components/async-tasks/async-task-ui";
import { extractAsyncTaskId, matchBoardMarketingPageGenerationTask } from "@/modern/components/async-tasks/utils";
import {
  isSandboxBoardRecord,
  TEST_DRIVE_SITE_PAGE_UNAVAILABLE_MSG,
} from "@/react/components/entityViews/marketingPages/utils";

export function useMarketingPageGenerate(workFileId?: number) {
  const { selectedBoard, viewManager } = useSwivuBryntumContext();
  const { pinTask } = useAsyncTaskContext();
  const { taskMatching } = useAsyncTaskUIContext();
  const boardRecord = selectedBoard?.record as
    | { slug?: string; id?: number; isSandbox?: boolean }
    | undefined;
  const slug = boardRecord?.slug;
  const boardId = boardRecord?.id;
  const isTestDrive = isSandboxBoardRecord(boardRecord);

  const generating =
    boardId != null &&
    taskMatching((task) =>
      matchBoardMarketingPageGenerationTask(task, boardId, workFileId),
    );

  const startGenerateForWorkFile = useCallback(
    async (targetWorkFileId: number) => {
      if (isTestDrive) {
        showToast({
          msg: TEST_DRIVE_SITE_PAGE_UNAVAILABLE_MSG,
          type: "error",
          duration: 4000,
        });
        return false;
      }
      if (!slug) {
        showToast({ msg: "No board selected.", type: "error", duration: 3000 });
        return false;
      }
      try {
        const response = await generateMarketingPage(slug, targetWorkFileId);
        const taskId = extractAsyncTaskId(response.data);
        const ok =
          response?.status === 202 ||
          response?.status === 200 ||
          taskId != null;
        if (!ok) {
          showToast({
            msg: "Could not start page generation.",
            type: "error",
            duration: 3000,
          });
          return false;
        }
        if (taskId != null && boardId != null) {
          pinTask(taskId, {
            type: ASYNC_TASK_TYPES.MARKETING_PAGE_GENERATION,
            meta_data: {
              board_id: boardId,
              work_file_id: targetWorkFileId,
            },
          });
        }
        const pagesView = viewManager?.getDefaultViewByType(VIEW_TYPES.marketingPages);
        if (pagesView) {
          viewManager?.selectView(pagesView);
        }
        return true;
      } catch (err: unknown) {
        showToast({
          msg: getApiErrorMessage(err, "Failed to start publishing your marketing page."),
          type: "error",
          duration: 4000,
        });
        return false;
      }
    },
    [slug, boardId, isTestDrive, pinTask, viewManager],
  );

  return { generating, isTestDrive, startGenerateForWorkFile };
}
